import { useMemo } from "react";
import useSWR from "swr";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import {
  LOSearchCondition,
  LOSearchOrderBy,
  SEARCH_TEXT_MIN_LENGTH,
} from "./types";

interface UseLOSearchParams {
  corporateId: string;
  conditions: LOSearchCondition[];
  searchText?: string;
  orderBy: LOSearchOrderBy[];
  page: number;
  pageSize: number;
  enabled?: boolean;
  catalogueFilters?: Record<string, unknown>;
  url?: string;
}

export const useLOSearch = ({
  corporateId,
  conditions,
  searchText,
  orderBy,
  page,
  pageSize,
  enabled = true,
  catalogueFilters,
  url: customUrl,
}: UseLOSearchParams) => {
  const url =
    enabled && corporateId
      ? customUrl
        ? customUrl
        : ServicesURL.getAggregatedLO.replace(":idCorporate", corporateId)
      : null;

  const trimmedSearchText = searchText?.trim() ?? "";
  const normalizedSearchText =
    trimmedSearchText.length >= SEARCH_TEXT_MIN_LENGTH
      ? trimmedSearchText
      : undefined;

  const body = useMemo(
    () => ({
      ...(normalizedSearchText ? { searchText: normalizedSearchText } : {}),
      conditions,
      orderBy,
      page: Math.max(page - 1, 0),
      size: pageSize,
      ...(catalogueFilters ? { catalogueFilters } : {}),
    }),
    [
      normalizedSearchText,
      conditions,
      orderBy,
      page,
      pageSize,
      catalogueFilters,
    ],
  );

  const { data, isLoading, error, mutate } = useSWR(
    url ? [url, body] : null,
    ([u, b]: [string, typeof body]) => fetcher(u, MethodType.POST, b),
  );

  const response: any = data;
  const items: any[] = response?.output ?? [];
  const totalItems: number =
    response?.metadata?.total_elements ?? response?.metadata?.total ?? 0;
  const totalPages: number =
    response?.metadata?.total_pages ??
    Math.max(Math.ceil(totalItems / pageSize), 1);

  return { items, totalItems, totalPages, isLoading, error, mutate };
};
